import { getRuntimeConfig } from '@/lib/runtimeConfig';
import { ApiProblem, type ProblemBody } from '@/api/problems';
import { recordServerDate } from '@/api/serverClock';
import { currentAccessToken } from '@/auth/AuthAdapter';

/**
 * The fetch wrapper — W-0.4.
 *
 * It never knows the mock exists. It talks HTTP to the configured base URL and
 * MSW intercepts at the network layer, so `VITE_USE_MOCKS=false` is the whole
 * of the swap to a real backend (§5.1).
 */

export const CORRELATION_HEADER = 'X-Correlation-Id';
export const IDEMPOTENCY_HEADER = 'Idempotency-Key';

/** `?page=&pageSize=` — the server caps pageSize at 200 (shared-contract §3). */
export const MAX_PAGE_SIZE = 200;
export const DEFAULT_PAGE_SIZE = 50;

/** 429 back-off. `Retry-After` wins when the server sends one. */
export const RATE_LIMIT_MAX_RETRIES = 3;
export const RATE_LIMIT_FALLBACK_DELAY_MS = 1_000;

export interface Page<T> {
  items: T[];
  page: number;
  pageSize: number;
  totalCount: number;
}

/** Body plus the response metadata a caller may need. */
export interface ApiResponse<T> {
  data: T;
  /** Present on configuration reads; feed it back as `ifMatch` on the write. */
  etag: string | null;
  correlationId: string | null;
  status: number;
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  /** JSON body. Serialised here; do not pre-stringify. */
  body?: unknown;
  /** Appended as search params. `undefined` and `null` entries are dropped. */
  query?: Record<string, string | number | boolean | undefined | null | readonly string[]>;
  /** Sent as `If-Match`. `412` when the server's ETag has moved on. */
  ifMatch?: string | null;
  /** Sent as `Idempotency-Key` on operational creates. */
  idempotencyKey?: string;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  /** Override the generated correlation id — used by tests. */
  correlationId?: string;
  /** Disable the 429 retry loop; used by tests that assert the raw response. */
  retryOnRateLimit?: boolean;
}

/**
 * How the client recovers an expired session.
 *
 * The access token lasts an hour. Before this existed, minute 61 of a shift
 * turned every poll into a 401 and every screen into an error state, with no
 * path back but a manual reload — the app had no 401 handling at all.
 *
 * Registered by `AuthProvider` rather than imported, because the auth layer
 * imports this file and a cycle between the two is how the token ends up
 * being read before it is set. Returns true when a new token is in hand.
 */
let sessionRefresher: (() => Promise<boolean>) | null = null;

export function setSessionRefresher(refresher: (() => Promise<boolean>) | null): void {
  sessionRefresher = refresher;
}

/** Paths that must never trigger a refresh — refreshing them is the loop. */
const NO_REFRESH = ['/auth/local/login', '/auth/local/refresh'];

function newCorrelationId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  return `pv-${Date.now().toString(16)}-${Math.random().toString(16).slice(2, 10)}`;
}

/** Exposed so the mock's `clientRequestId` and the outbox can share one source. */
export const newRequestId = newCorrelationId;

/**
 * The `Idempotency-Key` for an operational create (§9.9). Same generator, a
 * different name, because the two mean different things on the wire: the
 * request id travels in the body for a queued client, the idempotency key in
 * the header for a retried one.
 */
export const newIdempotencyKey = newCorrelationId;

function buildUrl(path: string, query: RequestOptions['query']): string {
  const base = getRuntimeConfig().apiBaseUrl.replace(/\/$/, '');
  const url = path.startsWith('http') ? path : `${base}${path.startsWith('/') ? path : `/${path}`}`;
  if (!query) return url;

  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue;
    if (Array.isArray(value)) {
      for (const entry of value) params.append(key, String(entry));
    } else {
      params.append(key, String(value));
    }
  }
  const qs = params.toString();
  return qs ? `${url}?${qs}` : url;
}

/**
 * Resolves a server-root-relative path the backend itself handed back (e.g. a report's
 * `downloadUrl`, already including whatever prefix the API lives under) against the actual
 * deployment origin — not the current page's. `buildUrl` above assumes its caller's path is
 * relative to `apiBaseUrl` (which already ends in that prefix); this is for the opposite case,
 * a path that already IS the server-root path, exactly as an `<a href>` would need it.
 *
 * The "one deployable" design (this file's own header comment) has the web bundle and the API
 * on the same origin, where a raw relative path already resolves correctly and this is a
 * no-op. Today the two are separate App Services (`Program.cs`'s CORS comment, `pv-app`), so
 * `apiBaseUrl` is an absolute URL — and a plain `<a href="/api/v1/...">` resolves against the
 * PAGE's origin, not the API's, which is exactly the bug this fixes.
 */
export function resolveServerUrl(path: string): string {
  if (path.startsWith('http')) return path;
  const base = getRuntimeConfig().apiBaseUrl;
  if (!base.startsWith('http')) return path;
  return new URL(path, base).toString();
}

/**
 * Downloads a permission-gated file link (e.g. a report run's `downloadUrl`) and triggers a
 * normal browser save. A plain `<a href>` cannot do this on its own: the route is gated like
 * every other one in this API, and a simple link navigation carries no `Authorization` header
 * — clicking it directly 401s regardless of `resolveServerUrl`. This fetches it with the
 * current bearer token attached instead, then hands the browser a blob to save, the same way
 * `downloadCsv` (`@/lib/csv`) already does for a client-built CSV string.
 */
export async function downloadAuthenticatedFile(
  path: string,
  fallbackFilename: string,
): Promise<void> {
  const correlationId = newCorrelationId();
  const endpoint = resolveServerUrl(path);

  const send = () => {
    // Read the token per attempt, for the same reason `request` does.
    const token = currentAccessToken();
    return fetch(endpoint, {
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        [CORRELATION_HEADER]: correlationId,
      },
    });
  };

  let response = await send();

  /*
   * This path does not go through `request`, so it needs the 401 recovery
   * spelled out again. A download is exactly where an expired session shows
   * up — a report run sits on screen until somebody gets round to fetching
   * it, which can easily be an hour after they queued it.
   */
  if (response.status === 401 && sessionRefresher && currentAccessToken()) {
    if (await sessionRefresher()) response = await send();
  }

  if (!response.ok) throw await readProblem(response, correlationId);

  const disposition = response.headers.get('Content-Disposition');
  const filename = disposition?.match(/filename="?([^";]+)"?/i)?.[1] ?? fallbackFilename;

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

function parseRetryAfter(header: string | null): number | undefined {
  if (!header) return undefined;
  const seconds = Number(header);
  if (Number.isFinite(seconds)) return seconds;
  const date = Date.parse(header);
  if (Number.isNaN(date)) return undefined;
  return Math.max(0, Math.round((date - Date.now()) / 1000));
}

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

async function readProblem(response: Response, correlationId: string): Promise<ApiProblem> {
  const retryAfterSeconds = parseRetryAfter(response.headers.get('Retry-After'));
  const contentType = response.headers.get('Content-Type') ?? '';

  if (contentType.includes('application/problem+json') || contentType.includes('json')) {
    try {
      const body = (await response.json()) as ProblemBody;
      return new ApiProblem(
        {
          ...body,
          status: body.status ?? response.status,
          // A server that forgets the traceId still leaves support something.
          traceId: body.traceId ?? correlationId,
        },
        retryAfterSeconds === undefined ? {} : { retryAfterSeconds },
      );
    } catch {
      // fall through to the synthetic problem below
    }
  }

  return new ApiProblem(
    {
      type: 'about:blank',
      title: response.statusText || 'Request failed',
      status: response.status,
      traceId: correlationId,
    },
    retryAfterSeconds === undefined ? {} : { retryAfterSeconds },
  );
}

/** The one call every query and mutation goes through. */
export async function request<T>(
  path: string,
  options: RequestOptions = {},
): Promise<ApiResponse<T>> {
  const {
    method = 'GET',
    body,
    query,
    ifMatch,
    idempotencyKey,
    headers: extraHeaders,
    signal,
    correlationId = newCorrelationId(),
    retryOnRateLimit = true,
  } = options;

  const url = buildUrl(path, query);

  const buildInit = (): RequestInit => {
    const headers: Record<string, string> = {
      Accept: 'application/json, application/problem+json',
      [CORRELATION_HEADER]: correlationId,
      ...extraHeaders,
    };
    // Read per attempt, not once: after a refresh the token captured up front
    // is precisely the one that just came back 401.
    const token = currentAccessToken();
    if (token) headers.Authorization = `Bearer ${token}`;
    if (body !== undefined) headers['Content-Type'] = 'application/json';
    if (ifMatch) headers['If-Match'] = ifMatch;
    if (idempotencyKey) headers[IDEMPOTENCY_HEADER] = idempotencyKey;

    const init: RequestInit = { method, headers };
    if (body !== undefined) init.body = JSON.stringify(body);
    if (signal) init.signal = signal;
    return init;
  };

  let attempt = 0;
  let refreshed = false;
  while (true) {
    const response = await fetch(url, buildInit());

    /*
     * One refresh, one retry, and only for a request that carried a token.
     * `refreshed` makes it once per request and `NO_REFRESH` keeps sign-in
     * and the refresh call itself out of it, so a dead session ends as a 401
     * the caller can act on rather than as a loop.
     */
    if (
      response.status === 401 &&
      !refreshed &&
      sessionRefresher &&
      currentAccessToken() &&
      !NO_REFRESH.some((entry) => path.startsWith(entry))
    ) {
      refreshed = true;
      if (await sessionRefresher()) continue;
    }

    if (response.status === 429 && retryOnRateLimit && attempt < RATE_LIMIT_MAX_RETRIES) {
      const retryAfter = parseRetryAfter(response.headers.get('Retry-After'));
      const delay =
        retryAfter !== undefined ? retryAfter * 1000 : RATE_LIMIT_FALLBACK_DELAY_MS * 2 ** attempt;
      attempt += 1;
      await sleep(delay);
      continue;
    }

    // Every response carries the server's clock; the freshness ladder needs
    // it to tell "old data" from "a wrong laptop clock".
    recordServerDate(response.headers.get('Date'));

    if (!response.ok) throw await readProblem(response, correlationId);

    const etag = response.headers.get('ETag');
    // The server echoes our correlation id; if it generated its own, take that.
    const echoed = response.headers.get(CORRELATION_HEADER) ?? correlationId;

    if (response.status === 204) {
      return { data: undefined as T, etag, correlationId: echoed, status: response.status };
    }

    const text = await response.text();
    const data = (text ? JSON.parse(text) : undefined) as T;
    return { data, etag, correlationId: echoed, status: response.status };
  }
}

/** `request` for the common case where only the body matters. */
export async function api<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { data } = await request<T>(path, options);
  return data;
}

export const apiGet = <T>(path: string, options: Omit<RequestOptions, 'method'> = {}) =>
  api<T>(path, { ...options, method: 'GET' });

export const apiPost = <T>(path: string, options: Omit<RequestOptions, 'method'> = {}) =>
  api<T>(path, { ...options, method: 'POST' });

export const apiPut = <T>(path: string, options: Omit<RequestOptions, 'method'> = {}) =>
  api<T>(path, { ...options, method: 'PUT' });

export const apiPatch = <T>(path: string, options: Omit<RequestOptions, 'method'> = {}) =>
  api<T>(path, { ...options, method: 'PATCH' });

export const apiDelete = <T>(path: string, options: Omit<RequestOptions, 'method'> = {}) =>
  api<T>(path, { ...options, method: 'DELETE' });
